import {
  BILLABLE_CALCULATION_LABELS, billableCalculationTotals, billableHoursTotal, billingNumber,
  groupBillableCalculationPosts,
} from './billingCalculation.js';
import { billablePrestationDetails, billableServiceContext, billingCustomerText as customerText } from './billingPrestationDetails.js';
import { groupBillableCalculationDrivers, summarizeBillableTime } from './billingCalculationDays.js';

export const CLIENT_INFORMATION_UNAVAILABLE = 'Information non disponible';
const UNITS = new Set(['heure', 'tonne', 'voyage']);
const SIDES = new Set(['loading', 'unloading']);
const CUSTOMER_NOTES = {
  rate_missing: 'Certains tarifs restent à confirmer.',
  billing_account_missing: 'L’entreprise facturée reste à confirmer.',
  billing_mapping_required: 'L’entreprise facturée reste à confirmer.',
};
const VALIDATION_NOTE = 'Certaines prestations nécessitent encore une validation.';

function serviceDate(value) {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return '';
  const parsed = new Date(`${value}T12:00:00Z`);
  return Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value ? value : '';
}

export function clientServiceDateLabel(value) {
  const date = serviceDate(value);
  return date ? new Intl.DateTimeFormat('fr-CA', { dateStyle: 'long', timeZone: 'UTC' })
    .format(new Date(`${date}T12:00:00Z`)) : 'Date non disponible';
}

function nonnegative(value) {
  const number = billingNumber(value);
  return number !== null && number >= 0 ? number : null;
}

function customerCurrency(source) {
  const currency = source.currency_code || 'CAD';
  if (!/^[A-Z]{3}$/.test(currency)) throw new Error('Devise indisponible');
  return currency;
}

function timestamp(value) {
  return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(value) && Number.isFinite(Date.parse(value))
    ? new Date(value).toISOString() : null;
}

function componentValues(component) {
  return {
    billing_unit: UNITS.has(component?.billing_unit) ? component.billing_unit : '',
    quantity: nonnegative(component?.quantity),
    unit_rate: nonnegative(component?.unit_rate),
    line_total: billingNumber(component?.line_total),
  };
}

function timeZone(value) {
  try { return typeof value === 'string' && value ? new Intl.DateTimeFormat('fr-CA', { timeZone: value }).resolvedOptions().timeZone : null; }
  catch { return null; }
}

// The driver is shown only when the client asks for the detail by driver.
function serviceCrew(crew) {
  const driver = customerText(crew?.driver_name);
  const truck = customerText(crew?.truck_number);
  return driver || truck ? { driver_name: driver || null, truck_number: truck || null, timezone: timeZone(crew?.timezone) } : null;
}

function freeze(value) {
  if (value && typeof value === 'object') {
    Object.values(value).forEach(freeze);
    Object.freeze(value);
  }
  return value;
}

function detailsForCustomer(item) {
  const facts = billablePrestationDetails(item);
  return {
    key: item.id, dateLabel: clientServiceDateLabel(item.service_date), reference: item.order_reference,
    customerName: item.service_context.customer_name, serviceContext: item.service_context,
    unit: item.billing_unit, quantity: item.quantity, rate: item.unit_rate,
    amount: facts.amount, currency: item.currency_code,
    hourly: facts.hourly, hasEvidence: Boolean(item.calculation_details),
    hasBlock: Boolean(facts.block), netSeconds: facts.net,
    travelSeconds: facts.travel, onsiteSeconds: facts.onsite, excludedSeconds: facts.block ? facts.excluded : null,
    // Only numeric inputs and known kinds reach the existing allocation formatter.
    allocation: facts.allocation.replaceAll('cette meunerie', 'ce client').replaceAll('du bloc attribué', 'de la durée attribuée'),
    exceptional: facts.exceptional,
  };
}

const CATEGORY_DESCRIPTIONS = {
  inspection: 'Temps d’inspection attribué à vos prestations.',
  loading: 'Déplacement et chargement.',
  delivery: 'Déplacement et livraison.',
  dompeur: 'Transport en camion dompeur, facturé par tonne livrée.',
  return: 'Part du retour attribuée à vos prestations.',
  preload: 'Préchargement attribué à vos prestations.',
  tonne_supplement: 'Frais complémentaires selon le tonnage.',
  voyage_supplement: 'Frais complémentaires par voyage.',
  fixed_fee: 'Montant fixe ajouté à la commande, par exemple une Aspiration.',
};

function groupCategories(posts) {
  const categories = new Map();
  for (const post of posts) {
    const key = post.kind || `legacy:${post.side}`;
    if (!categories.has(key)) categories.set(key, {
      key: String(categories.size), kind: post.kind, label: post.label,
      description: CATEGORY_DESCRIPTIONS[post.kind] || 'Prestations attribuées à vos commandes.', posts: [],
    });
    categories.get(key).posts.push(post);
  }
  return [...categories.values()].map((category) => {
    const quantities = new Map();
    const rates = new Set();
    let missingRate = false;
    for (const post of category.posts) {
      if (!quantities.has(post.unit)) quantities.set(post.unit, 0);
      const previous = quantities.get(post.unit);
      quantities.set(post.unit, previous === null || post.quantity === null ? null : previous + post.quantity);
      missingRate ||= post.rate === null || !post.unit;
      rates.add(JSON.stringify([post.rate, post.unit, post.currency]));
    }
    const first = category.posts[0];
    return {
      ...category,
      quantities: [...quantities].map(([unit, quantity]) => ({ unit, quantity })),
      rateState: missingRate ? 'missing' : rates.size > 1 ? 'variable' : 'single',
      rate: { value: first.rate, unit: first.unit, currency: first.currency },
      // Each post already contains only its own saved, rounded component amounts.
      totals: billableCalculationTotals(category.posts.map((post) => ({
        line_total: post.amount, currency_code: post.currency,
      }))),
    };
  });
}

/** Project the current panel, without retaining source objects, identities or free-form notes. */
export function buildBillingClientExplanation(sourceItems) {
  if (!Array.isArray(sourceItems) || sourceItems.some((item) => !item || typeof item !== 'object')) {
    throw new Error('Sélection indisponible');
  }
  const revisions = new Map();
  const notes = new Set();
  const accountNames = new Set();
  const clientNames = new Set();
  const accountIds = new Set();
  let missingAccount = false;
  const accountName = (source) => customerText(source.billing_account_name || source.billing_account?.name);
  // Ligne libre d'un relevé (rabais…) : son libellé et son montant, sans journée, quantité ni tarif.
  const freeLines = sourceItems.filter((source) => source.is_manual).map((source, index) => {
    if (accountName(source)) accountNames.add(accountName(source));
    return { key: String(index), label: customerText(source.description) || 'Ajustement',
      amount: billingNumber(source.line_total), currency: customerCurrency(source) };
  });
  const items = sourceItems.filter((source) => !source.is_manual).map((source, index) => {
    const name = accountName(source);
    if (name) accountNames.add(name);
    else missingAccount = true;
    const accountId = source.billing_account_id || source.billing_account?.id;
    if (accountId) accountIds.add(accountId);
    const clientName = customerText(source.source_client?.client_name || source.client_name);
    if (clientName) clientNames.add(clientName);
    const reasons = Array.isArray(source.blocking_reasons) ? source.blocking_reasons : [];
    for (const reason of reasons) {
      const code = typeof reason === 'string' ? reason : reason?.code || reason?.reason;
      notes.add(Object.hasOwn(CUSTOMER_NOTES, code) ? CUSTOMER_NOTES[code] : VALIDATION_NOTE);
    }
    if (['blocked', 'incomplete'].includes(source.eligibility_status) && !reasons.length) notes.add(VALIDATION_NOTE);
    const evidence = source.calculation_details;
    const revision = evidence?.revision_id;
    if (revision && !revisions.has(revision)) revisions.set(revision, String(revisions.size + 1));
    const block = evidence?.block;
    return {
      id: String(index), source_type: ['sheet', 'preload'].includes(source.source_type) ? source.source_type : 'legacy',
      service_date: serviceDate(source.service_date),
      order_reference: customerText(source.order_reference),
      service_context: billableServiceContext(source), service_crew: serviceCrew(source.service_crew),
      billing_side: SIDES.has(source.billing_side) ? source.billing_side : '',
      ...componentValues(source), currency_code: customerCurrency(source), pricing_pending: Boolean(source.pricing_pending),
      billing_components: Array.isArray(source.billing_components) ? source.billing_components.map(componentValues) : [],
      calculation_details: evidence ? {
        revision_id: revisions.get(revision) || null,
        // Un Chargé PM se raconte le jour de son chargement, avec le reste de cette journée.
        execution_date: serviceDate(evidence.execution_date) || null,
        // Un Chargé PM saisi par son seul « Quitté à » compte en chargement, route comprise.
        pm_delivery_period: ['day', 'evening'].includes(evidence.pm_delivery_period) ? evidence.pm_delivery_period : null,
        kind: Object.hasOwn(BILLABLE_CALCULATION_LABELS, evidence.kind) ? evidence.kind : '',
        source_seconds: nonnegative(evidence.source_seconds),
        coefficient: evidence.coefficient ? {
          numerator: nonnegative(evidence.coefficient.numerator), denominator: nonnegative(evidence.coefficient.denominator),
        } : null,
        block: block ? {
          start: timestamp(block.start), arrival: timestamp(block.arrival), end: timestamp(block.end),
          gross_seconds: nonnegative(block.gross_seconds), net_seconds: nonnegative(block.net_seconds),
          travel_seconds: nonnegative(block.travel_seconds), onsite_seconds: nonnegative(block.onsite_seconds),
        } : null,
        exceptional_rate: Boolean(evidence.exceptional_rate),
      } : null,
    };
  });
  // This action is for one account card, never a combined statement for different accounts.
  if (accountIds.size > 1 || accountNames.size > 1 || (accountNames.size === 0 && clientNames.size > 1)) {
    throw new Error('Sélection de plusieurs entreprises');
  }
  // Le nom d'un autre client n'est jamais présenté : un départ n'est nommé que s'il est un lieu de cette sélection.
  const ownSites = new Set(items.flatMap((item) => item.service_context.destination.map((location) => location.name)).filter(Boolean));
  for (const { service_context: context } of items) {
    if (context.origin_kind === 'visit' && !context.origin.every((location) => ownSites.has(location.name))) context.origin = [];
  }
  const posts = groupBillableCalculationPosts(items).map((post, index) => ({
    key: index, kind: post.kind, label: post.label, side: post.side,
    unit: post.unit, quantity: post.quantity, rate: post.rate, amount: post.amount, currency: post.currency,
    details: post.items.map(detailsForCustomer),
  }));
  const totals = billableCalculationTotals([...items, ...freeLines.map((line) => ({ line_total: line.amount, currency_code: line.currency }))]);
  if (items.length && missingAccount) notes.add('L’entreprise facturée reste à confirmer.');
  if (posts.some((post) => post.rate === null)) notes.add(CUSTOMER_NOTES.rate_missing);
  if (totals.some((total) => total.amount === null)) notes.add('Le total reste à déterminer tant que certains montants sont inconnus.');
  if (items.some((item) => !item.service_date)) notes.add('La date de certaines prestations est à confirmer.');
  if (items.some((item) => !item.calculation_details)) notes.add('Le détail du parcours n’est pas disponible pour certaines prestations.');
  if (posts.some((post) => post.quantity === null || !post.unit)) notes.add('Certaines quantités ou unités sont à confirmer.');
  const dates = items.map((item) => item.service_date).filter(Boolean).sort();
  const time = summarizeBillableTime(items);
  const categories = groupCategories(posts);
  return freeze({
    clientName: [...accountNames][0] || [...clientNames][0] || CLIENT_INFORMATION_UNAVAILABLE,
    accountConfirmed: Boolean(accountNames.size) && !missingAccount,
    itemCount: items.length + freeLines.length, freeLines, orderCount: new Set(items.map((item) => item.order_reference).filter(Boolean)).size,
    firstDate: dates[0] || '', lastDate: dates.at(-1) || '',
    totals, hours: billableHoursTotal(items), posts, categories, notes: [...notes],
    time, drivers: groupBillableCalculationDrivers(time.days),
    // Hours are explained by the time summary; only charges in other units keep their own table.
    extraCategories: groupCategories(posts.filter((post) => post.unit !== 'heure')),
  });
}
